import { UIElementContentSource } from '@smart-tv/hooks/use-ui-element-content/use-ui-element-content';
import {
  ContentListProviders,
  UIElement,
  UIElementStyle,
  UniverseSortingValue
} from '@smart-tv/typings/content';

import { kmmRequest } from './kmm';

interface RawSpecialGenre {
  SpecialGenreId: string | number;
  SpecialGenreName: string;
  SpecialGenreActiveFromVersion: string;
  SpecialGenreHasRemovableContent: string;
  SpecialGenreRemoveCmd: string;
  SpecialGenreType: string;
}

interface RawGenre {
  StofaMainGenreId: string | number;
  GenreName: string;
  ImageType: string;
  StofaSubGenreId: string | number;
}

interface RawResponse {
  SpecialGenres?: RawSpecialGenre[];
  Genres: RawGenre[];
}

const baseProps = {
  sortingType: UniverseSortingValue.NEWEST,
  itemLimit: 30
};

const mapSpecialGenre = (genre: RawSpecialGenre, universeId: number): UIElement => ({
  id: Number(genre.SpecialGenreId),
  title: genre.SpecialGenreName,
  style: genre.SpecialGenreType.toUpperCase() as UIElementStyle,
  contentSource: UIElementContentSource.SpecialUniverse,
  providerContentProps: {
    universeId,
    subGenreId: 0,
    mainGenreId: Number(genre.SpecialGenreId),
    ...baseProps
  }
});

const mapGenre = (genre: RawGenre, index: number, universeId: number): UIElement => ({
  id: universeId * 10 + index,
  title: genre.GenreName,
  style: genre.ImageType.toUpperCase() as UIElementStyle,
  contentSource: UIElementContentSource.Universe,
  providerContentProps: {
    universeId,
    subGenreId: genre.StofaSubGenreId ? Number(genre.StofaSubGenreId) : 0,
    mainGenreId: Number(genre.StofaMainGenreId),
    ...baseProps
  }
});

const responseMapper = (response: RawResponse, universeId: number): UIElement[] => {
  const specialGenres = (response.SpecialGenres ?? []).map((genre) =>
    mapSpecialGenre(genre, universeId)
  );
  const genres = response.Genres.map((genre, index) => mapGenre(genre, index, universeId));

  return specialGenres.concat(genres);
};

export async function getContentGenres(
  universeId: number,
  providers: ContentListProviders
): Promise<UIElement[]> {
  const jsonResponse = await kmmRequest('getContentGenres', { universeId, providers });
  const response = JSON.parse(jsonResponse) as RawResponse;
  return responseMapper(response, universeId);
}

export default getContentGenres;
